import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { authStore } from '@/stores/AuthStore'
import { CpuChipIcon } from '@heroicons/react/24/outline'

const AuthCallbackPage = observer(() => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const handleCallback = async () => {
      // Check for error from backend
      const errorParam = searchParams.get('error')
      const errorMessage = searchParams.get('message')

      if (errorParam) {
        setError(errorMessage || `Authentication failed: ${errorParam}`)
        return
      }

      // Check for tokens from backend
      const accessToken = searchParams.get('access_token')
      const refreshToken = searchParams.get('refresh_token')

      if (!accessToken || !refreshToken) {
        setError('Missing authentication tokens')
        return
      }

      try {
        // Store tokens and fetch user info
        await authStore.handleOAuthCallback(accessToken, refreshToken)
        navigate('/dashboard', { replace: true })
      } catch (err: any) {
        setError(authStore.error || 'Authentication failed')
      }
    }

    handleCallback()
  }, [searchParams, navigate])

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-secondary-50 to-primary-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl mb-4">
              <CpuChipIcon className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-foreground">Authentication Failed</h1>
          </div>

          <div className="bg-card rounded-2xl shadow-xl p-8">
            <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg mb-6">
              <p className="text-sm text-red-600">{error}</p>
            </div>

            <button
              onClick={() => navigate('/login', { replace: true })}
              className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Return to Login
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-secondary-50 to-primary-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-2xl mb-4 animate-pulse">
            <CpuChipIcon className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">Signing you in...</h1>
          <p className="text-muted-foreground mt-2">Please wait while we complete your authentication</p>
        </div>

        <div className="bg-card rounded-2xl shadow-xl p-8">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            <span className="ml-3 text-muted-foreground">Processing authentication...</span>
          </div>
        </div>
      </div>
    </div>
  )
})

export default AuthCallbackPage
